import React, { useEffect, useRef, useState } from 'react';
import { HelpCircle, ExternalLink, X } from 'lucide-react';
import { DOLE_SOURCES } from '../lib/doleSources';

/**
 * A small "?" help affordance. Click to open a plain-language explanation of a dashboard
 * element, ending in a link to the exact official DOLE government page. Content comes from
 * lib/doleSources.js so every link is verified in one place.
 *
 * Usage: <HelpTip id="placement" />
 */
export default function HelpTip({ id, className = '' }) {
  const src = DOLE_SOURCES[id];
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const b = btnRef.current?.getBoundingClientRect();
      if (!b) return;
      const width = Math.min(320, window.innerWidth - 20);
      let left = Math.min(Math.max(8, b.left - 8), window.innerWidth - width - 8);
      const popH = popRef.current?.offsetHeight || 180;
      let top = b.bottom + 6;
      if (top + popH > window.innerHeight - 8) top = Math.max(8, b.top - popH - 6);
      setPos({ top, left, width });
    };
    place();
    const onDoc = (e) => {
      if (popRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  if (!src) return null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label={`What is this? ${src.title}`}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className={`inline-grid place-items-center h-4 w-4 rounded-full border border-slate-300 text-slate-500 text-[10px] font-extrabold leading-none bg-white hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 shrink-0 align-middle ${className}`}
      >
        ?
      </button>
      {open && (
        <div
          ref={popRef}
          role="dialog"
          aria-label={src.title}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 60 }}
          className="rounded-xl border border-slate-200 bg-white shadow-xl p-3"
        >
          <button
            type="button"
            aria-label="Close"
            onClick={() => setOpen(false)}
            className="absolute top-2 right-2 text-slate-400 hover:text-slate-700"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <div className="font-semibold text-sm text-slate-900 mb-1 pr-5 flex items-center gap-1.5">
            <HelpCircle className="h-4 w-4 text-blue-700 shrink-0" /> {src.title}
          </div>
          <div className="text-xs text-slate-600 leading-relaxed">{src.body}</div>
          {src.source && <div className="text-[11px] text-slate-400 italic mt-1.5">Source: {src.source}</div>}
          <a
            href={src.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:underline"
          >
            <ExternalLink className="h-3 w-3" /> {src.linkLabel || 'Read the official DOLE page'}
          </a>
        </div>
      )}
    </>
  );
}
